import { lazy, Suspense, useEffect, useState } from "react";
import { Outlet, useLocation, useNavigate } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import type { AccountSummary } from "@shared/api";
import { useServerEvents } from "@web/lib/events";
import {
  consumeExplicitSignOut,
  queryKeys,
  useLogout,
  useSession,
  useStartGoogleConnect,
  useSyncNow,
} from "@web/lib/queries";
import { useOwnerTimeZone } from "@web/lib/settingsApi";
import { toggleTheme } from "@web/lib/theme";
import { setDisplayTimeZone } from "@web/lib/time";
import { undoLatest, useUndoAvailable } from "@web/lib/undoRegistry";
import { composeStore } from "@web/compose/composeStore";
import { MakeTimeHost } from "@web/day/MakeTimeDialog";
import { useMeasureSession } from "@web/measure/useMeasure";
import { VerdictHost } from "@web/measure/VerdictDialog";
import { TeachHost } from "@web/teach/TeachChooser";
import { parseListSearch } from "@web/inbox/listSearch";
import { BriefBanner } from "@web/notify/BriefBanner";
import { NotificationsDrawer } from "@web/notify/NotificationsDrawer";
import { notifyStore } from "@web/notify/notifyStore";
import { loadCompose, preloadScreens } from "@web/screens";
import { CommandPalette } from "./CommandPalette";
import { useCommand } from "./commands";
import { Dock } from "./Dock";
import { shellChrome } from "./layout";
import { BottomNav, Sidebar } from "./Nav";
import { ShortcutsHelp } from "./ShortcutsHelp";
import { TopBar } from "./TopBar";
import { useKeyBindings } from "./useKeyBindings";

/** Commands that work on every signed-in screen. */
function useGlobalCommands(openPalette: () => void, openHelp: () => void) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const logout = useLogout();
  const connect = useStartGoogleConnect();
  const syncNow = useSyncNow();

  useCommand("palette.open", openPalette);
  useCommand("help.shortcuts", openHelp);
  useCommand("nav.feed", () => void navigate({ to: "/feed" }));
  useCommand("nav.inbox", () => void navigate({ to: "/inbox" }));
  useCommand("nav.tasks", () => void navigate({ to: "/tasks" }));
  useCommand("nav.brief", () => void navigate({ to: "/brief" }));
  useCommand("nav.notifications", () => notifyStore.toggle());
  useCommand("nav.work", () => void navigate({ to: "/work" }));
  useCommand("nav.learning", () => void navigate({ to: "/learning" }));
  useCommand("nav.hidden", () => void navigate({ to: "/hidden" }));
  useCommand("nav.recent", () => void navigate({ to: "/recent" }));
  useCommand("nav.freshStart", () => void navigate({ to: "/fresh-start" }));
  useCommand("nav.screener", () => void navigate({ to: "/screener" }));
  useCommand("nav.spend", () => void navigate({ to: "/settings/spend" }));
  useCommand("nav.vips", () => void navigate({ to: "/settings/vips" }));
  useCommand("nav.pushSettings", () => void navigate({ to: "/settings/notifications" }));
  useCommand("nav.calendar", () => void navigate({ to: "/settings/calendar" }));
  useCommand("nav.config", () => void navigate({ to: "/feed/config" }));
  useCommand("nav.audit", () => void navigate({ to: "/audit" }));
  useCommand("nav.drafts", () => void navigate({ to: "/compose" }));
  useCommand("nav.saved", () => void navigate({ to: "/saved" }));
  useCommand("nav.answers", () => void navigate({ to: "/answers" }));
  useCommand("nav.ask", () => void navigate({ to: "/ask", search: {} }));
  useCommand("nav.measure", () => void navigate({ to: "/settings/measure" }));
  // `c` opens the compose sheet; sending inside it is arm → confirm, never one key.
  useCommand("compose.new", () => composeStore.open({}));
  useCommand("undo.last", () => void undoLatest(), useUndoAvailable());
  useCommand("nav.accounts", () => void navigate({ to: "/accounts" }));
  useCommand("nav.settings", () => void navigate({ to: "/settings" }));
  useCommand("theme.toggle", toggleTheme);
  useCommand("accounts.connect", () =>
    connect.mutate(
      {},
      { onError: () => toast("Couldn't start the Google connection. Try again.") },
    ),
  );
  useCommand("accounts.syncAll", () => {
    const accounts =
      queryClient
        .getQueryData<AccountSummary[]>(queryKeys.accounts)
        ?.filter((a) => a.status !== "disabled") ?? [];
    if (!accounts.length) {
      toast("No connected accounts to sync yet.");
      return;
    }
    for (const a of accounts) syncNow.mutate(a.id);
    toast(
      accounts.length === 1 ? "Checking for new mail…" : `Checking ${accounts.length} accounts…`,
    );
  });
  // The shell notices the signed-out session and moves to the sign-in page.
  useCommand("session.logout", () => logout.mutate());
}

// The composer is its own chunk, fetched right after the shell (it is always mounted, closed),
// so it never holds up the first paint.
const ComposeSheet = lazy(() => loadCompose().then((m) => ({ default: m.ComposeSheet })));

export function AppShell() {
  const session = useSession();
  const navigate = useNavigate();
  const location = useLocation();
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);

  const authenticated = session.data?.authenticated === true;
  useServerEvents(authenticated);
  // Every time on screen reads in the owner's zone. Set while rendering (it is idempotent) so the
  // screens below format with it in this same pass.
  setDisplayTimeZone(useOwnerTimeZone());
  useEffect(preloadScreens, []);
  useKeyBindings();
  // Local timings (U0): visits and active minutes, sent only to this MeeMail server.
  useMeasureSession(authenticated);
  useGlobalCommands(
    () => setPaletteOpen((open) => !open),
    () => setHelpOpen(true),
  );

  // Session ended mid-visit (logout elsewhere, expiry): go to the right door.
  useEffect(() => {
    const info = session.data;
    if (!info || info.authenticated) return;
    if (info.setupRequired) void navigate({ to: "/setup" });
    else if (consumeExplicitSignOut()) void navigate({ to: "/login" });
    else void navigate({ to: "/login", search: { redirect: location.href } });
    // location.href is read at the moment the session flips; no need to re-run on navigation.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session.data, navigate]);

  const listSearch = parseListSearch(location.search as Record<string, unknown>);
  const chrome = shellChrome(location.pathname);

  return (
    <div className="flex h-dvh overflow-hidden bg-bg text-ink">
      {chrome.sidebar ? <Sidebar pathname={location.pathname} /> : null}
      <div className="flex min-w-0 flex-1 flex-col">
        {chrome.topBar ? (
          <TopBar pathname={location.pathname} search={listSearch} signedIn={authenticated} />
        ) : null}
        {chrome.briefBanner ? (
          <BriefBanner pathname={location.pathname} enabled={authenticated} />
        ) : null}
        <main className="relative min-h-0 flex-1">
          <Outlet />
        </main>
        {authenticated && chrome.dock ? <Dock /> : null}
        {chrome.bottomNav ? <BottomNav pathname={location.pathname} /> : null}
      </div>
      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} />
      <ShortcutsHelp open={helpOpen} onClose={() => setHelpOpen(false)} />
      <NotificationsDrawer enabled={authenticated} />
      <Suspense fallback={null}>
        <ComposeSheet enabled={authenticated} />
      </Suspense>
      {authenticated ? <MakeTimeHost /> : null}
      {authenticated ? <VerdictHost /> : null}
      {authenticated ? <TeachHost /> : null}
    </div>
  );
}
